import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Badge, Button, Input } from '@components'
import { IPricingPlan } from '@query'
import { formatEurosWithOpts } from '@utils'

type BillingInterval = 'month' | 'year'

interface Props {
  plan: IPricingPlan
  interval: BillingInterval
  trialAvailable: boolean
  hasPaymentMethod: boolean
  isContinueDisabled?: boolean
  isSubmitting?: boolean
  onBack: () => void
  onContinue: () => void
}

function getPlanPrice(plan: IPricingPlan, interval: BillingInterval): number {
  return interval === 'year' ? plan.yearly_price_eur ?? 0 : plan.monthly_price_eur ?? 0
}

export const OrderReviewStep: React.FC<Props> = ({
  plan,
  interval,
  trialAvailable,
  hasPaymentMethod,
  isContinueDisabled = false,
  isSubmitting = false,
  onBack,
  onContinue
}) => {
  const [t] = useTranslation()
  const {
    register,
    clearErrors,
    formState: { errors }
  } = useFormContext<{
    promotion_code: string
  }>()
  const promotionCodeField = register('promotion_code')
  const recurringTotal = getPlanPrice(plan, interval)
  const displayTotal = trialAvailable ? 0 : recurringTotal
  const recurringTotalLabel = formatEurosWithOpts(recurringTotal, { avoidDecimals: true })
  const submitLabel = hasPaymentMethod
    ? trialAvailable
      ? t('components.customPricingTable.startTrial')
      : t('components.customPricingTable.summary.purchase')
    : t('components.customPricingTable.summary.continueToPayment')

  return (
    <Wrapper>
      <Panel>
        <SectionTitle>{t('components.customPricingTable.summary.title')}</SectionTitle>

        <SummaryList>
          <SummaryLine>
            <span>
              {t(
                interval === 'year'
                  ? 'components.customPricingTable.summary.planYearly'
                  : 'components.customPricingTable.summary.planMonthly',
                { name: plan.name }
              )}
            </span>
            <span>
              {formatEurosWithOpts(getPlanPrice(plan, interval), { avoidDecimals: true })}
            </span>
          </SummaryLine>

          <SummaryTotal>
            <div>
              <span>{t('components.customPricingTable.summary.totalYearly')}</span>
              {trialAvailable && <FootnoteMarker aria-hidden="true">{' *'}</FootnoteMarker>}
            </div>

            {trialAvailable ? (
              <TrialTotal>
                <StruckPrice>{recurringTotalLabel}</StruckPrice>
                <TrialPrice>
                  {formatEurosWithOpts(displayTotal, { avoidDecimals: true })}
                </TrialPrice>
                <Badge type="info">{t('components.customPricingTable.trialBadge')}</Badge>
              </TrialTotal>
            ) : (
              <span>{formatEurosWithOpts(displayTotal, { avoidDecimals: true })}</span>
            )}
          </SummaryTotal>
        </SummaryList>

        <PromotionCodeField>
          <Input
            name="promotion_code"
            label={t('components.customPricingTable.summary.promotionCodeLabel')}
            placeholder={t('components.customPricingTable.summary.promotionCodePlaceholder')}
            errors={errors.promotion_code}
            autoComplete="off"
            {...promotionCodeField}
            disabled={isSubmitting}
            onChange={event => {
              promotionCodeField.onChange(event)
              clearErrors('promotion_code')
            }}
          />
        </PromotionCodeField>

        <ActionRow>
          <Button type="button" isSecondary onClick={onBack} disabled={isSubmitting}>
            {t('components.customPricingTable.summary.backToPlans')}
          </Button>
          <Button
            type="button"
            intent={hasPaymentMethod ? 'success' : 'primary'}
            onClick={onContinue}
            disabled={isContinueDisabled || isSubmitting}
            showSpinner={isSubmitting}
          >
            {submitLabel}
          </Button>
        </ActionRow>

        {trialAvailable && (
          <TrialNotice>
            *{' '}
            {t(
              interval === 'year'
                ? 'components.customPricingTable.summary.trialContinuesYearly'
                : 'components.customPricingTable.summary.trialContinuesMonthly',
              { amount: recurringTotalLabel }
            )}
          </TrialNotice>
        )}
      </Panel>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 36rem;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const SummaryTotal = styled(SummaryLine)`
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 600;
`

const TrialTotal = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

const StruckPrice = styled.span`
  color: ${({ theme }) => theme.colors.metalGray};
  text-decoration: line-through;
`

const TrialPrice = styled.span`
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const FootnoteMarker = styled.span`
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const TrialNotice = styled.p`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xl}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

const PromotionCodeField = styled.div`
  max-width: 20rem;
`

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;
`
